"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.crudAggregateRefine = exports.crudAggregateShape = exports.countHavingLeaves = exports.crudHavingSchema = exports.crudAggregateExprSchema = exports.crudGroupByExprSchema = void 0;
const zod_1 = require("zod");
const aggregate_types_1 = require("./aggregate-types");
/**
 * Zod-схемы агрегационного запроса `q.<entity>.aggregate`.
 *
 * Архитектура повторяет filter-DSL (`filter-zod.ts`): closed enums на поля и
 * функции, recursive builder с manual depth counter для HAVING, лимиты на
 * размеры. Cross-field инварианты (коллизии алиасов, ссылки having/orderBy
 * на существующие алиасы) — в `crudAggregateRefine`, который defineEntity
 * вешает `.superRefine()` на итоговую query-схему (envelope + тело).
 */
/**
 * IANA-таймзона ('Europe/Moscow', 'UTC'). Узкий синтаксический фильтр —
 * семантическую валидность проверит Postgres (`AT TIME ZONE $n` — параметр,
 * не идентификатор: невалидная tz даст SQL error, не injection).
 */
const tzSchema = zod_1.z
    .string()
    .max(64)
    .regex(/^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+)*$/);
const aliasNameSchema = zod_1.z.string().regex(aggregate_types_1.CRUD_AGGREGATE_ALIAS_RE);
/**
 * Схема одного groupBy-выражения: `{field}` | `{timeBucket}` | `{extract}`.
 * `.strict()` — запрет лишних ключей (typo / obfuscation).
 *
 * Возврат cast'ится к `z.ZodType<CrudGroupByExpr<TFields[number]>>` — zod не
 * выводит точный union из динамически собранных веток (тот же приём, что
 * `typedFilterSchema` в defineEntity); runtime-валидация полная.
 */
const crudGroupByExprSchema = (fields) => {
    const fieldEnum = zod_1.z.enum(fields);
    const schema = zod_1.z.union([
        zod_1.z.object({ field: fieldEnum }).strict(),
        zod_1.z
            .object({
            timeBucket: zod_1.z
                .object({
                field: fieldEnum,
                interval: zod_1.z.enum(aggregate_types_1.CrudTimeBucketIntervals),
                tz: tzSchema.optional(),
            })
                .strict(),
        })
            .strict(),
        zod_1.z
            .object({
            extract: zod_1.z
                .object({
                field: fieldEnum,
                part: zod_1.z.enum(aggregate_types_1.CrudExtractParts),
                tz: tzSchema.optional(),
            })
                .strict(),
        })
            .strict(),
    ]);
    return schema;
};
exports.crudGroupByExprSchema = crudGroupByExprSchema;
/**
 * Схема одного агрегата.
 *
 * Два варианта union'а:
 * - `{fn:'count', where?}` — единственная функция без `field` (`COUNT(*)`);
 * - `{fn, field, where?}` — все функции с колонкой (вкл. `count` =
 *   `COUNT(col)`, не считает NULL).
 *
 * `where` — переданная filter-схема entity (тот же whitelist, те же лимиты,
 * что у корневого `filter`).
 */
const crudAggregateExprSchema = (fields, filterSchema) => {
    const fieldEnum = zod_1.z.enum(fields);
    const schema = zod_1.z.union([
        zod_1.z
            .object({
            fn: zod_1.z.literal('count'),
            where: filterSchema.optional(),
        })
            .strict(),
        zod_1.z
            .object({
            fn: zod_1.z.enum(aggregate_types_1.CrudAggregateFunctions),
            field: fieldEnum,
            where: filterSchema.optional(),
        })
            .strict(),
    ]);
    return schema;
};
exports.crudAggregateExprSchema = crudAggregateExprSchema;
/**
 * Recursive HAVING-схема (по образцу `crudFilterSchema`, но мельче):
 * leaf `{field: <алиас>, op: <числовой>, value: number}` | and/or/not.
 *
 * `field` здесь — АЛИАС агрегата (динамический ключ `aggr`), поэтому regex,
 * не z.enum; принадлежность реальному набору алиасов проверяет
 * `crudAggregateRefine`.
 */
const crudHavingSchema = (limits = {}) => {
    const lim = { ...aggregate_types_1.DEFAULT_CRUD_AGGREGATE_LIMITS, ...limits };
    const leafSchema = zod_1.z
        .object({
        field: aliasNameSchema,
        op: zod_1.z.enum(aggregate_types_1.CrudHavingOperators),
        value: zod_1.z.number().finite(),
    })
        .strict();
    const buildNodeSchema = (depth) => {
        if (depth >= lim.maxHavingDepth)
            return leafSchema;
        const childSchema = buildNodeSchema(depth + 1);
        return zod_1.z.union([
            leafSchema,
            zod_1.z.object({ and: zod_1.z.array(childSchema).min(1) }).strict(),
            zod_1.z.object({ or: zod_1.z.array(childSchema).min(1) }).strict(),
            zod_1.z.object({ not: childSchema }).strict(),
        ]);
    };
    return buildNodeSchema(0).superRefine((value, ctx) => {
        const leaves = (0, exports.countHavingLeaves)(value);
        if (leaves > lim.maxHavingLeaves) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: `Having has too many leaf conditions: ${leaves} (max ${lim.maxHavingLeaves})`,
            });
        }
    });
};
exports.crudHavingSchema = crudHavingSchema;
/** Walker по HAVING-AST — количество leaf-условий (для leaves-cap). */
const countHavingLeaves = (ast) => {
    if (ast.and)
        return ast.and.reduce((sum, n) => sum + (0, exports.countHavingLeaves)(n), 0);
    if (ast.or)
        return ast.or.reduce((sum, n) => sum + (0, exports.countHavingLeaves)(n), 0);
    if (ast.not)
        return (0, exports.countHavingLeaves)(ast.not);
    if (ast.field)
        return 1;
    return 0;
};
exports.countHavingLeaves = countHavingLeaves;
/**
 * Shape (z.ZodRawShape) тела агрегационного запроса — для spread'а в
 * `QueryMessage.extend({type, version, ...crudAggregateShape(...)})`.
 *
 * После extend caller ОБЯЗАН повесить `.superRefine(crudAggregateRefine)` —
 * cross-field инварианты (см. ниже) на уровне shape невыразимы.
 *
 * @param fields — тот же tuple, что в `crudFilterSchema` entity.
 * @param filterSchema — `entity.filterSchema` (переиспользуется для `filter`
 *   запроса и `where` агрегатов). Тип filter-поля наследуется из неё
 *   (`z.ZodType<CrudFilterAst<F>>` — как `typedFilterSchema` в defineEntity).
 */
const crudAggregateShape = (fields, filterSchema, limits = {}) => {
    const lim = { ...aggregate_types_1.DEFAULT_CRUD_AGGREGATE_LIMITS, ...limits };
    const groupByExpr = (0, exports.crudGroupByExprSchema)(fields);
    const aggrExpr = (0, exports.crudAggregateExprSchema)(fields, filterSchema);
    const shape = {
        filter: filterSchema.optional(),
        groupBy: zod_1.z
            .record(aliasNameSchema, groupByExpr)
            .refine((obj) => Object.keys(obj).length >= 1, { message: 'groupBy must not be empty (omit it for a single-row total)' })
            .refine((obj) => Object.keys(obj).length <= lim.maxGroupByKeys, {
            message: `groupBy has too many keys (max ${lim.maxGroupByKeys})`,
        })
            .optional(),
        aggr: zod_1.z
            .record(aliasNameSchema, aggrExpr)
            .refine((obj) => Object.keys(obj).length >= 1, { message: 'aggr must contain at least one aggregate' })
            .refine((obj) => Object.keys(obj).length <= lim.maxAggregates, {
            message: `aggr has too many keys (max ${lim.maxAggregates})`,
        }),
        having: (0, exports.crudHavingSchema)(lim).optional(),
        orderBy: zod_1.z
            .array(zod_1.z
            .object({
            field: aliasNameSchema,
            dir: zod_1.z.enum(['asc', 'desc']),
        })
            .strict())
            .min(1)
            .optional(),
        limit: zod_1.z.number().int().positive().max(lim.maxGroups).optional(),
        offset: zod_1.z.number().int().nonnegative().optional(),
    };
    return shape;
};
exports.crudAggregateShape = crudAggregateShape;
/**
 * Cross-field инварианты агрегационного запроса — для `.superRefine()` на
 * итоговой схеме (envelope + shape):
 *
 * 1. Алиасы groupBy и aggr не пересекаются (иначе двусмысленная колонка в reply).
 * 2. Каждый `having`-leaf ссылается на существующий алиас **агрегата**
 *    (по groupBy-колонкам фильтрует `filter`, не having).
 * 3. Каждый `orderBy.field` — существующий алиас groupBy ∪ aggr.
 */
const crudAggregateRefine = (q, ctx) => {
    const groupAliases = Object.keys(q.groupBy ?? {});
    const aggrAliases = Object.keys(q.aggr ?? {});
    for (const alias of groupAliases) {
        if (aggrAliases.includes(alias)) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: `Alias '${alias}' is used in both groupBy and aggr`,
                path: ['aggr', alias],
            });
        }
    }
    const checkHavingRefs = (node) => {
        if (node.and) {
            node.and.forEach(checkHavingRefs);
            return;
        }
        if (node.or) {
            node.or.forEach(checkHavingRefs);
            return;
        }
        if (node.not) {
            checkHavingRefs(node.not);
            return;
        }
        if (node.field !== undefined && !aggrAliases.includes(node.field)) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: `having references unknown aggregate alias '${node.field}'`,
                path: ['having'],
            });
        }
    };
    if (q.having)
        checkHavingRefs(q.having);
    const allAliases = [...groupAliases, ...aggrAliases];
    for (const entry of q.orderBy ?? []) {
        if (!allAliases.includes(entry.field)) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: `orderBy references unknown alias '${entry.field}'`,
                path: ['orderBy'],
            });
        }
    }
};
exports.crudAggregateRefine = crudAggregateRefine;
//# sourceMappingURL=aggregate-zod.js.map